/**
 * Single owner of every window.Intercom interaction.
 *
 * Intercom is loaded and booted by loadIntercom below, called from Root.js once
 * marketing consent allows it. Loading it and *showing* the launcher are
 * separate concerns: the
 * launcher stays hidden and the messenger is opened from our own triggers
 * (the S.O.S. panels, the navbar dropdown, the mobile tab bar), so the bubble
 * cannot sit on top of UI in the bottom corners. The launcher is revealed again
 * only while the guest has an unread reply, so an agent's answer is never lost.
 */

/** Gap between the launcher/messenger and the bottom of the viewport, in px. */
export const DEFAULT_VERTICAL_PADDING = 20;

/** How long to wait for the messenger to actually open before falling back. */
const OPEN_TIMEOUT_MS = 1200;

/** Poll interval while waiting for the loader to define window.Intercom. */
const READY_POLL_MS = 500;

/** Stop polling after this long rather than retrying for the whole session. */
const READY_TIMEOUT_MS = 30000;

let messengerOpen = false;
let callbacksRegistered = false;

/**
 * window.Intercom being defined does not prove the messenger works — the loader
 * snippet installs a queue stub before widget.js boots — but its absence does
 * prove it cannot, which is the common case (marketing consent declined, so GTM
 * never loads Intercom at all).
 * @returns {boolean}
 */
export const isIntercomAvailable = () => typeof window.Intercom === 'function';

/**
 * Runs cb once window.Intercom exists, polling until it does.
 * @param {(intercom: Function) => void} cb
 * @returns {() => void} cleanup
 */
export const whenIntercomReady = cb => {
  let cancelled = false;
  let timer;
  const startedAt = Date.now();

  const attempt = () => {
    if (cancelled) return;
    if (isIntercomAvailable()) {
      cb(window.Intercom);
      return;
    }
    if (Date.now() - startedAt >= READY_TIMEOUT_MS) return;
    timer = window.setTimeout(attempt, READY_POLL_MS);
  };

  attempt();

  return () => {
    cancelled = true;
    if (timer) window.clearTimeout(timer);
  };
};

/**
 * Merges settings into the booted Intercom instance. Partial updates are safe:
 * anything left out keeps its current value.
 * @param {Object} settings
 * @returns {boolean} whether the update reached Intercom
 */
export const updateIntercom = settings => {
  if (!isIntercomAvailable()) return false;
  window.Intercom('update', settings);
  return true;
};

/**
 * @param {boolean} visible
 * @returns {boolean}
 */
export const setLauncherVisible = visible => updateIntercom({ hide_default_launcher: !visible });

/**
 * @param {number} padding px between the launcher and the bottom of the viewport
 * @returns {boolean}
 */
export const setVerticalPadding = padding => updateIntercom({ vertical_padding: padding });

/**
 * Intercom keeps one handler per event name, so these are registered once and
 * the result kept in module state rather than per component.
 * @returns {void}
 */
const registerCallbacks = () => {
  if (callbacksRegistered || !isIntercomAvailable()) return;
  window.Intercom('onShow', () => {
    messengerOpen = true;
  });
  window.Intercom('onHide', () => {
    messengerOpen = false;
  });
  callbacksRegistered = true;
};

/**
 * Subscribes to the guest's unread reply count. Fires immediately with the
 * current count on registration, then on every change. Intercom keeps one
 * handler per event name, so call this once rather than per render.
 * @param {(count: number) => void} cb
 * @returns {boolean} whether the subscription reached Intercom
 */
export const onUnreadCount = cb => {
  if (!isIntercomAvailable()) return false;
  window.Intercom('onUnreadCountChange', count => cb(count || 0));
  return true;
};

/**
 * Opens the messenger, calling onUnavailable if it cannot be opened.
 *
 * onUnavailable also fires when Intercom is present but never actually shows —
 * ad blockers routinely allow GTM through while blocking widget.intercom.io,
 * which leaves the loader's queue stub swallowing 'show' forever.
 * @param {{ onUnavailable?: () => void, timeoutMs?: number }} [options]
 * @returns {boolean} whether 'show' was dispatched at all
 */
export const openChat = ({ onUnavailable, timeoutMs = OPEN_TIMEOUT_MS } = {}) => {
  if (!isIntercomAvailable()) {
    if (onUnavailable) onUnavailable();
    return false;
  }

  registerCallbacks();
  messengerOpen = false;
  window.Intercom('show');

  if (onUnavailable) {
    window.setTimeout(() => {
      if (!messengerOpen) onUnavailable();
    }, timeoutMs);
  }

  return true;
};

/** Set once the widget has been requested, so a second call cannot double-boot. */
let loadRequested = false;

/**
 * Loads and boots the Intercom widget.
 *
 * Booting from here rather than from a GTM tag keeps support chat off the
 * tag-trigger path: a tag that fires on page view never runs when consent is
 * granted on that same page, which left guests with no chat at all until they
 * refreshed by hand.
 *
 * Skips silently when Intercom is already present, so this is safe to run while
 * the GTM container still has its own Intercom tag — whichever arrives first
 * wins and the other stands down. Two *boots*, though, tear down an open
 * messenger, so the GTM tag should be retired rather than left racing this.
 *
 * @param {string} appId Intercom workspace id
 * @returns {boolean} whether a load was started
 */
export const loadIntercom = appId => {
  if (!appId || loadRequested || isIntercomAvailable()) return false;
  loadRequested = true;

  const script = document.createElement('script');
  script.src = `https://widget.intercom.io/widget/${appId}`;
  script.async = true;
  script.onload = () => {
    if (!isIntercomAvailable()) return;
    // Hidden from the start: BookingFooterWrapper owns when the launcher shows.
    window.Intercom('boot', { app_id: appId, hide_default_launcher: true });
  };
  document.body.appendChild(script);

  return true;
};
